import React from 'react';
import styled from '@emotion/styled';
import { RefreshCw, ChevronsUp, FileText, BookOpen, Search, Calendar } from 'lucide-react';

const ContentSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FieldDocumentResults = ({ 
  documentResults, 
  hoveredDocument, 
  setHoveredDocument,
  onValueSelect,
  selectedDocument = null,
  showDetailedView = false,
  setShowDetailedView,
  detailedDocumentIndex = null,
  setDetailedDocumentIndex
}) => {
  
  // Create tooltip content for info icon
  const createTooltipContent = (result) => {
    const sections = [];
    
    // Normalization info
    if (result.normalization && result.normalization.source !== 'null') {
      const sourceEmoji = result.normalization.source === 'calculated' ? '🧮' : 
                         result.normalization.source === 'lookup' ? '🔍' : 
                         result.normalization.source === 'direct' ? '✓' : 'ℹ️';
      
      sections.push(`${sourceEmoji} ${result.normalization.source.toUpperCase()}`);
      
      if (result.normalization.reasoning) {
        sections.push(`   ${result.normalization.reasoning}`);
      }
      
      // Add numeric value if available
      if (result.numValue !== null && result.numValue !== undefined) {
        const numericDisplay = typeof result.numValue === 'number' ? 
          (result.numValue * 100).toFixed(4) + '%' : result.numValue;
        sections.push(`   Numeric: ${numericDisplay}`);
      }
      
      sections.push(''); // Empty line for spacing
    }
    
    // Match info
    if (result.matchSource && result.matchSource !== 'not_found') {
      const matchEmoji = result.matchSource === 'algorithmic' ? '🔢' : '🤖';
      sections.push(`${matchEmoji} ${result.matchSource.toUpperCase()} MATCH`);
      
      if (result.matchReasoning) {
        sections.push(`   ${result.matchReasoning}`);
      }
      
      sections.push(''); // Empty line for spacing
    }
    
    // Source context
    if (result.source_context) {
      sections.push('📄 SOURCE CONTEXT:');
      sections.push(`   "${result.source_context}"`);
    }
    
    return sections.join('\n');
  };

  // Show detailed view for a specific document
  if (showDetailedView && detailedDocumentIndex !== null) {
    const result = documentResults[detailedDocumentIndex];
    
    return (
      <ContentSection>
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '1rem',
          width: '100%'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '2rem'
          }}>
            <div>
              <h2 style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#1f2937'
              }}>
                {result.document}
              </h2>
              <p style={{
                margin: '0.25rem 0 0 0',
                fontSize: '0.875rem',
                color: '#6b7280'
              }}>
                Detailed Analysis
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <button
                onClick={() => onValueSelect(result, detailedDocumentIndex)}
                style={{
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#2563eb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#3b82f6';
                }}
              >
                Select This Value
              </button>
              <button
                onClick={() => {
                  setShowDetailedView(false);
                  setDetailedDocumentIndex(null);
                }}
                style={{
                  background: '#d1d5db',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#9ca3af';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#d1d5db';
                }}
              >
                Close
              </button>
            </div>
          </div>

          {/* Two-column layout */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
            {/* Left Column */}
            <div>
              {/* Extracted Value */}
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '0.75rem'
                }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6b7280'
                  }}>
                    <FileText size={16} />
                  </div>
                  <h3 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#374151',
                    margin: 0
                  }}>
                    Extracted Value
                  </h3>
                </div>
                <div style={{
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  padding: '0.75rem',
                  fontSize: '0.875rem',
                  color: '#374151',
                  lineHeight: '1.4'
                }}>
                  {result.textValue || result.value}
                </div>
              </div>

              {/* Page Number */}
              {result.page && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '0.75rem'
                  }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#6b7280'
                    }}>
                      <BookOpen size={16} />
                    </div>
                    <h3 style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#374151',
                      margin: 0
                    }}>
                      Page Number
                    </h3>
                  </div>
                  <div style={{
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '0.75rem',
                    fontSize: '0.875rem',
                    color: '#374151',
                    lineHeight: '1.4'
                  }}>
                    Page {result.page}
                  </div>
                </div>
              )}

              {/* Source Context */}
              {result.source_context && (
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '0.75rem'
                  }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#6b7280'
                    }}>
                      <FileText size={16} />
                    </div>
                    <h3 style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#374151',
                      margin: 0
                    }}>
                      Source Context
                    </h3>
                  </div>
                  <div style={{
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '0.75rem',
                    fontSize: '0.875rem',
                    color: '#374151',
                    lineHeight: '1.4'
                  }}>
                    "{result.source_context}"
                  </div>
                </div>
              )}
            </div>

            {/* Right Column */}
            <div>
              {/* Lookup Normalization */}
              {result.normalization && result.normalization.source !== 'null' && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '0.75rem'
                  }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#6b7280'
                    }}>
                      <Search size={16} />
                    </div>
                    <h3 style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#374151',
                      margin: 0
                    }}>
                      Lookup Normalization
                    </h3>
                  </div>
                  <div style={{
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '0.75rem'
                  }}>
                    {result.normalization.reasoning && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <div style={{
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          color: '#6b7280',
                          marginBottom: '0.25rem'
                        }}>
                          Reasoning:
                        </div>
                        <div style={{
                          fontSize: '0.875rem',
                          color: '#374151',
                          lineHeight: '1.4'
                        }}>
                          {result.normalization.reasoning}
                        </div>
                      </div>
                    )}
                    {result.numValue !== null && result.numValue !== undefined && (
                      <div style={{ marginTop: '1rem' }}>
                        <div style={{
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          color: '#6b7280',
                          marginBottom: '0.5rem'
                        }}>
                          Numeric Value:
                        </div>
                        <span style={{
                          display: 'inline-block',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#4b5563',
                          background: 'transparent',
                          border: '1px solid #4b5563'
                        }}>
                          {typeof result.numValue === 'number' ? 
                            (result.numValue * 100).toFixed(4) + '%' : result.numValue}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Algorithmic Match Analysis */}
              {result.matchSource && result.matchSource !== 'not_found' && (
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '0.75rem'
                  }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#6b7280'
                    }}>
                      <Calendar size={16} />
                    </div>
                    <h3 style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#374151',
                      margin: 0
                    }}>
                      Algorithmic Match Analysis
                    </h3>
                  </div>
                  <div style={{
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '0.75rem'
                  }}>
                    <div style={{ marginBottom: '1rem', marginTop: '1rem' }}>
                      <div style={{
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        color: '#6b7280',
                        marginBottom: '0.5rem'
                      }}>
                        Match Type:
                      </div>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        color: 'white',
                        background: result.matchType === 'perfect' ? '#10b981' : 
                                   result.matchType === 'partial' ? '#f59e0b' : '#ef4444'
                      }}>
                        {result.matchType === 'perfect' ? 'Perfect Match' : 
                         result.matchType === 'partial' ? 'Partial Match' : 'No Match'}
                      </span>
                    </div>
                    {result.matchReasoning && (
                      <div>
                        <div style={{
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          color: '#6b7280',
                          marginBottom: '0.25rem'
                        }}>
                          Logic:
                        </div>
                        <div style={{
                          fontSize: '0.875rem',
                          color: '#374151',
                          lineHeight: '1.4'
                        }}>
                          {result.matchReasoning}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </ContentSection>
    );
  }

  return (
    <ContentSection>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1rem'
      }}>
        <div style={{ 
          fontSize: '0.875rem', 
          color: '#6b7280', 
          lineHeight: '1.5'
        }}>
          Results from re-running field extraction across all scanned documents:
        </div>
        <button
          style={{
            background: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            padding: '0.5rem 1rem',
            fontSize: '0.875rem',
            color: '#6b7280',
            cursor: 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            opacity: 0.6
          }}
          disabled
          title="Rescan functionality coming tomorrow"
        >
          <RefreshCw size={14} />
          Rescan Documents
        </button>
      </div>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'nowrap', marginBottom: '1.5rem' }}>
        {documentResults.map((result, index) => (
          <div 
            key={index} 
            data-cy="document-result" 
            className={selectedDocument === index ? 'selected' : ''}
            style={{
              flex: '1',
              minWidth: '160px',
              background: selectedDocument === index ? '#f0f9ff' : 'white',
              border: selectedDocument === index ? '2px solid #3b82f6' : '1px solid #e5e7eb',
              borderRadius: '8px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
            onMouseEnter={() => result.status === 'found' && setHoveredDocument(index)}
            onMouseLeave={() => result.status === 'found' && setHoveredDocument(null)}
          >
            {/* Double chevron for "use this value" or invisible spacer */}
            <div style={{
              position: 'relative',
              height: '40px', // Fixed height to prevent layout shift
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              background: 'white'
            }}>
              {result.status === 'found' ? (
                <ChevronsUp 
                  size={16} 
                  style={{ 
                    color: '#6b7280',
                    cursor: 'pointer',
                    position: 'absolute',
                    transform: hoveredDocument === index ? 'translateY(-4px) scale(1.1)' : 'translateY(0) scale(1)',
                    transition: 'transform 0.2s ease'
                  }}
                  onClick={() => onValueSelect(result, index)}
                />
              ) : (
                <div style={{ width: '16px', height: '16px' }} />
              )}
            </div>
            
            {/* Document name button */}
            <div 
              style={{
                background: result.status === 'found' ? '#475569' : '#64748b',
                color: 'white',
                padding: '0.5rem 0.75rem',
                textAlign: 'center',
                fontSize: '0.75rem',
                fontWeight: '500',
                borderRadius: '12px',
                margin: '0.5rem',
                display: 'inline-block',
                width: 'calc(100% - 1rem)',
                cursor: result.status === 'found' ? 'pointer' : 'default',
                transform: hoveredDocument === index ? 'scale(1.05)' : 'scale(1)',
                transition: 'all 0.2s ease'
              }}
              onClick={() => {
                if (result.status === 'found') {
                  onValueSelect(result, index);
                }
              }}
            >
              {result.document}
            </div>
            
            {/* Match Status Bar - moved here */}
            {result.status === 'found' && (
              <div style={{ padding: '0 0.5rem' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.25rem'
                }}>
                  <span style={{
                    fontSize: '0.75rem',
                    color: result.matchType === 'perfect' ? '#10b981' : 
                           result.matchType === 'partial' ? '#f59e0b' : '#ef4444',
                    fontWeight: '500'
                  }}>
                    {result.matchType === 'perfect' ? 'Perfect Match' : 
                     result.matchType === 'partial' ? 'Partial Match' : 
                     'No Match'}
                  </span>
                  
                        {/* Info icon - clickable for detailed view */}
                        <div 
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            background: '#3b82f6',
                            color: 'white',
                            fontSize: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={() => {
                            setShowDetailedView(true);
                            setDetailedDocumentIndex(index);
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#2563eb';
                            e.target.style.transform = 'scale(1.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#3b82f6';
                            e.target.style.transform = 'scale(1)';
                          }}
                          title="Click for detailed analysis"
                        >
                          i
                        </div>
                </div>
                
                {/* Confidence bar */}
                <div style={{
                  width: '100%',
                  height: '6px',
                  background: '#e5e7eb',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${result.confidence * 100}%`,
                    height: '100%',
                    background: result.matchType === 'perfect' ? '#10b981' : 
                               result.matchType === 'partial' ? '#f59e0b' : '#ef4444',
                    borderRadius: '3px',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>
            )}
            
            {/* Card body */}
            <div style={{
              padding: '0.75rem',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              height: '100%'
            }}>
              {result.status === 'found' ? (
                <>
                  {/* Expandable content area */}
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                    minHeight: 0 // Allow flex item to shrink
                  }}>
                    {/* Text Value Display */}
                    <div style={{
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: '#1f2937',
                      padding: '0.75rem',
                      background: '#f3f4f6',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      textAlign: 'center',
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '1rem' // Material Design spacing
                    }}>
                      {result.textValue || result.value}
                    </div>
                    
                    {/* Page number display */}
                    {result.page && (
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        textAlign: 'center',
                        marginBottom: '0.5rem'
                      }}>
                        Page {result.page}
                      </div>
                    )}
                  </div>
                  
                  
                  {/* Select Button with Material Design spacing */}
                  <button
                    onClick={() => onValueSelect(result, index)}
                    style={{
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '0.75rem 1rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#2563eb';
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#3b82f6';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
                    }}
                  >
                    Select
                  </button>
                </>
              ) : (
                <div style={{
                  fontSize: '0.875rem',
                  color: '#6b7280',
                  fontStyle: 'italic',
                  textAlign: 'center',
                  padding: '0.75rem',
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  Field not found
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </ContentSection>
  );
};

export default FieldDocumentResults;
